import type { BaseEnvelope, BasePatchOp } from '../janus-engine/compile/base-patch';
import type { AppliedNewEntity } from '../janus-engine/transport/applied-frame';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { encounterCreatureField } from '../shared/array-types';
import { fetchSavedResponse, readSavedResponse } from '../shared/read-saved-response';
import { relocationDefinitionsUnchanged, relocationMemberMatches, type RelocationMemberBatch, type RelocationMemberFailure } from '../janus-engine/compile/saved-creature-relocation';
import { storedStatusRows } from '../janus-engine/compile/creature-status-reader';
import { COMPOUND_LINK_STATUSES, statusItemId } from '../janus-engine/inventory/lift-occupancy';

type Row = Record<string, unknown>;
type Prepared = { entity: AppliedNewEntity; at: number; generation: string };
const prepared = new Map<string, Prepared>();
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const generation = (): string => {
    const store = (window as Window & { __ftDefinitionStore?: { epoch?: number; cacheGeneration?: number } }).__ftDefinitionStore;
    return JSON.stringify([store?.epoch, store?.cacheGeneration]);
};
declare global { interface Window {
    ftCompileRelocationMembers?: (envelopes: readonly BaseEnvelope[], destination?: Row) => Promise<RelocationMemberBatch>;
    ftWarmRelocationCompilers?: (count: number) => void;
} }

/** A single live-row read; the owning websocket compiler shares one worker batch and catalog read. */
export async function readRelocationMembers(ids: readonly string[], destination?: Row): Promise<RelocationMemberBatch> {
    const wanted = [...new Set(ids.map(upper))], members: AppliedNewEntity[] = [], failures: RelocationMemberFailure[] = [];
    // The lanes boot during the read, not after it.
    if (wanted.length) window.ftWarmRelocationCompilers?.(wanted.length);
    for (let offset = 0; offset < wanted.length; offset += 64) {
        const batch = wanted.slice(offset, offset + 64);
        try {
            const response = await fetchSavedResponse('/options/api/object_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: batch }), signal: AbortSignal.timeout(90000) });
            const payload = await readSavedResponse(response);
            if (!response.ok || !fromBaseRecord(payload) || !fromBaseRecord(payload.objects)) throw new Error('relocation-member-batch:read');
            const objects = payload.objects;
            const envelopes = batch.flatMap(id => {
                const host = objects[id], envelope = fromBaseRecord(host) ? host._entity_base : undefined;
                if (!fromBaseRecord(envelope) || !fromBaseRecord(envelope.base)
                    || !['creature', 'pc'].includes(String(envelope.entity)) || upper(envelope.entity_id) !== id) {
                    failures.push({ id, reason: 'relocation-member-batch:member-unavailable' }); return [];
                }
                return [envelope as unknown as BaseEnvelope];
            });
            // Compound bodies must be judged with their complete source, before changing either side's context.
            const blockedSources = new Set(destination ? envelopes.filter(envelope => Number(encounterCreatureField(envelope.base.encounter_creatures, 'encounter_creature_dead') ?? 0) === 1
                || ['target', 'caster'].some(side => storedStatusRows(envelope.base, side as 'target' | 'caster').some(status => COMPOUND_LINK_STATUSES.has(statusItemId(status)))))
                .map(envelope => upper(encounterCreatureField(envelope.base.encounter_creatures, 'encounter_id'))) : []);
            const independent = envelopes.filter(envelope => !blockedSources.has(upper(encounterCreatureField(envelope.base.encounter_creatures, 'encounter_id'))));
            if (!independent.length) continue;
            if (!window.ftCompileRelocationMembers) throw new Error('relocation-member-batch:compiler-unavailable');
            const compiled = await window.ftCompileRelocationMembers(independent, destination);
            members.push(...compiled.members); failures.push(...compiled.failures);
        } catch (error) {
            for (const id of batch) if (!failures.some(failure => failure.id === id) && !members.some(member => upper(member.entity_id) === id))
                failures.push({ id, reason: error instanceof Error ? error.message : 'relocation-member-batch:read' });
        }
    }
    return { members, failures };
}

/** Compile in the destination's saved environment, then borrow the original parent/revision for placement CAS. */
export async function prepareRelocationMembers(ids: readonly string[], destination: Row): Promise<{ rows: Map<string, Row>; failures: readonly RelocationMemberFailure[] }> {
    const readGeneration = generation();
    const batch = await readRelocationMembers(ids, destination), rows = new Map<string, Row>(), failures = [...batch.failures];
    for (const [id, prior] of prepared) if (Date.now() - prior.at > 120000) prepared.delete(id);
    for (const entity of batch.members) {
        const source = fromBaseRecord(entity.base.relocation_before) ? entity.base.relocation_before : undefined;
        if (!source) { failures.push({ id: upper(entity.entity_id), reason: 'relocation-member-batch:source-unavailable' }); continue; }
        const { relocation_before: _before, ...base } = entity.base;
        const saved = { ...entity, base };
        prepared.set(upper(entity.entity_id), { entity: saved, at: Date.now(), generation: readGeneration });
        rows.set(upper(entity.entity_id), { ...entity.member, catalog: entity.member.catalog ?? entity.base.catalog,
            encounter_creatures: { ...entity.member.encounter_creatures as Row,
            encounter_id: encounterCreatureField(source, 'encounter_id'), movement_rev: Number(encounterCreatureField(source, 'movement_rev') ?? 0) } });
    }
    while (prepared.size > 256) prepared.delete(prepared.keys().next().value!);
    return { rows, failures };
}

/** The saved echo can consume this page's exact preview. Other viewers read the arriving members together, once. */
export async function loadRelocationMembers(ids: readonly string[], ops: readonly BasePatchOp[]): Promise<RelocationMemberBatch> {
    const found: AppliedNewEntity[] = [], missing: string[] = [];
    const revisions = Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})) as Record<string, number>;
    for (const id of ids) {
        const prior = prepared.get(upper(id)), op = ops.find(op => upper(op.row_id) === upper(id) && op.table === 'encounter_creatures' && op.op === 'upsert');
        const unchangedDefinitions = prior && await relocationDefinitionsUnchanged(prior.entity.base, ops);
        const destination = prior && upper(encounterCreatureField(prior.entity.base.encounter_creatures, 'encounter_id'));
        const dependencies = prior?.entity.base.dependency_revs as Record<string, number> | undefined;
        if (prior && op && Date.now() - prior.at < 120000 && prior.generation === generation()
            && unchangedDefinitions && (revisions[upper(id)] === undefined || revisions[upper(id)] === prior.entity.base_rev) && relocationMemberMatches(prior.entity.base, op.cols)
            && (!destination || revisions[destination] === undefined || revisions[destination] === dependencies?.[destination])
            && upper(encounterCreatureField(prior.entity.base.encounter_creatures, 'encounter_id')) === upper(encounterCreatureField(op.cols, 'encounter_id'))
            && Number(encounterCreatureField(prior.entity.base.encounter_creatures, 'movement_rev') ?? 0) === Number(encounterCreatureField(op.cols, 'movement_rev') ?? 0)) {
            found.push(prior.entity); prepared.delete(upper(id));
        }
        else missing.push(id);
    }
    const batch = missing.length ? await readRelocationMembers(missing) : { members: [], failures: [] };
    return { members: [...found, ...batch.members], failures: batch.failures };
}
